/**
 * 地址审核池 · 前端类型（对应技术方案 §4.5 pickup_address_review_pool）
 * P6.2 阶段：仅用于前端 mock 渲染，后端契约就绪后以 dubbo/GraphQL DTO 对齐替换。
 */

/** 来源标签：1 系统进单 / 2 BD 录入 / 3 历史存量 */
export enum ReviewSourceTag {
  System = 1,
  Bd = 2,
  History = 3,
}

/** 诊断类型：1 新地址 / 2 标准化失败 / 3 相似 */
export enum ReviewDiagnosisType {
  New = 1,
  StandardizeFailed = 2,
  Similar = 3,
}

export interface ReviewContactPerson {
  name?: string;
  phoneCountryCode?: string;
  phone?: string;
  /** 是否已拉黑 */
  inBlacklist?: boolean;
}

/** 审核池待处理地址条目 */
export interface ReviewPoolItem {
  id: number;
  warehouseId: number; // hub
  /** 标准化 + dock 拼接后的展示地址 */
  displayAddress: string;
  /** 拆分地址（建站/更新站点保存时透传给 saveCooperationWarehouse，由其内部重标准化） */
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  dock?: string;
  contactPersons: ReviewContactPerson[];
  /** 当日该地址所有订单渠道汇总（展示名） */
  channelNames: string[];
  /** 当日该地址所有订单渠道 id（AB 映射选渠道用；解析自 channelIdsJson） */
  channelIds?: number[];
  sourceTag: ReviewSourceTag;
  diagnosisType: ReviewDiagnosisType;
  /** 地址标准化状态：0未标准化 1成功 2失败（diagnosisType=StandardizeFailed 由本字段驱动） */
  standardizeStatus?: number;
  /** 相似诊断时的相似站点数（diagnosisType=Similar 有效） */
  similarCount?: number;
  /** 历史存量对应的既有站点 ID（sourceTag=History 有效） */
  relatedWarehouseId?: number;
  /** 历史存量关联站点名称（随 relatedWarehouseId 由后端一并返回，详情头展示用） */
  relatedWarehouseName?: string;
  /** 该地址首次下单时间（= 创建时间），ISO 字符串 */
  firstOrderTime: string;
  /** 相似判定 hash（组合分组键，去全部小号 + 归一） */
  addressAnalysisHash: string;
  /** 条目命中键 core_hash（2.1 需求点1 建站复用，跳过地址库重算） */
  addressCoreHash?: string;
  /** 相似诊断时的相似站点列表（diagnosisType=Similar 有效） */
  similarStations?: SimilarStation[];
  /** 历史存量：当日该站点是否已有预约单（决定确认预约单 / 创建预约单） */
  hasTodayAppointment?: boolean;
  /** 历史存量当日预约单 id（供「确认预约单」按 id 只读加载真实单；无当日单为 undefined） */
  todayAppointmentId?: number;
  /**
   * 当日单量（2.1 需求点6）：该地址今天要揽收的订单数。
   *
   * 「今天」按 **hub 时区**算（运营视角），与 hasTodayAppointment 的「今天」(UTC，与建单/合单
   * 调度链路同基准) 刻意不同，两者错开一天是正确的。
   * 仅系统进单/BD 录入展示；历史存量恒 0（golive 直插、不涉及订单），不展示。
   */
  todayOrderCount?: number;
  /**
   * 联系结果草稿（2.1 需求点1）：服务端存在审核池条目上，供两步电联流程跨会话/跨人续做。
   *
   * <p>为什么不是前端本地存：ImageUploader 多图模式画不出没有 displayUrls 的既有 value，
   * 而上传后的预览地址是 blob: URL（刷新即失效）；且 localStorage 按浏览器存，
   * 隔天做二次电联换人/换设备就丢。详见 plan §2.5.2。
   */
  contactDraft: ReviewContactDraftData;
  /**
   * 地址异常上报（2.2 需求点1）。为 undefined 表示当前没有有效上报。
   *
   * <p>上报后该地址免司机上门、直接进二次建联；有值时卡片展示「已上报地址异常 + 时间 + 原因」
   * 并放出「编辑 / 撤销上报」。撤销后回到 undefined。
   */
  abnormalReport?: ReviewAbnormalReportData;
}

/** 条目上的地址异常上报数据（2.2 需求点1）。 */
export interface ReviewAbnormalReportData {
  /**
   * 上报时间，**原始 ISO-8601 串**（展示格式化由 AbnormalReportCard 内部统一做，调用方不要预格式化）。
   *
   * <p>可空：站点侧的记录从 operation_log 推导，历史/异常数据可能缺时间。
   * 「当前是否有有效上报」的判据不在本字段 —— 审核池侧看 dto.abnormalReportedAt 是否有值、
   * 站点侧看最新一条 type=12 的 action 是否为 revoke（见 activeAbnormalReport）。
   */
  reportedAt?: string;
  /** 原因 code（ROAD_ADDRESS / NO_UNIT_NUMBER / OTHER），展示时经 i18n 转文案 */
  reason?: string;
  remark?: string;
  /** 提交时原样回传的 objectKey */
  proofKeys: string[];
  /**
   * 预签名可读地址（ImageUploader 的 displayUrls），按 proofKeys 同序。
   *
   * <p><b>可能比 proofKeys 短</b>：服务端对个别预签名失败的 key 直接跳过（那一张不展示）。
   * 所以不要按下标与 proofKeys 配对后补空串 —— OnsiteProofs 是「urls 非空就整组按 urls 渲染」，
   * 混入空串就是裂图。
   */
  proofUrls: string[];
  reportedByName?: string;
}

/** 条目上的联系结果草稿数据（两组：首联 / 二联，各含结果 + 截图 key + 可读地址）。 */
export interface ReviewContactDraftData {
  contact?: ReviewContactResult;
  /** 提交时原样回传的 objectKey */
  proofKeys: string[];
  /** 与 proofKeys 等长同序的预签名可读地址（ImageUploader 的 displayUrls） */
  proofUrls: string[];
  secondContact?: ReviewContactResult;
  secondProofKeys: string[];
  secondProofUrls: string[];
}

/**
 * 服务端联系结果编码 ↔ 前端字面量。
 *
 * <p>库里存 1/2（tinyint），前端用 'contacted'/'not_contacted'。两处转换必须共用这一对函数，
 * 各写一遍很容易一边写反 —— 写反的后果是运营回来看到的联系结果与他上次选的相反，
 * 而门禁与操作分支全靠它，会把人导到完全错误的操作上。
 */
export const CONTACT_RESULT_CONTACTED = 1;
export const CONTACT_RESULT_NOT_CONTACTED = 2;

export function toContactResult(code?: number | null): ReviewContactResult | undefined {
  if (code === CONTACT_RESULT_CONTACTED) return 'contacted';
  if (code === CONTACT_RESULT_NOT_CONTACTED) return 'not_contacted';
  return undefined;
}

export function fromContactResult(v?: ReviewContactResult): number | undefined {
  if (v === 'contacted') return CONTACT_RESULT_CONTACTED;
  if (v === 'not_contacted') return CONTACT_RESULT_NOT_CONTACTED;
  return undefined;
}

/** 联系结果 */
export type ReviewContactResult = 'contacted' | 'not_contacted';

/** 可执行操作 */
export enum ReviewOperation {
  CreateSite = 'create_site', // 新建站点
  AbMapping = 'ab_mapping', // AB 仓映射
  MergeToExisting = 'merge', // 合并到已有
  UpdateSite = 'update_site', // 更新站点（历史存量）
  ConfirmAppointment = 'confirm_appointment', // 确认预约单（历史存量·未联系到·当日有单）
  CreateAppointment = 'create_appointment', // 创建预约单（历史存量·未联系到·当日无单）
  ModifyAddress = 'modify_address', // 修改地址（标准化失败）
}

/** 相似站点（相似诊断时展示） */
export interface SimilarStation {
  id: number;
  name?: string;
  address: string;
  contact?: string;
  /**
   * 建联状态：1待上门 2揽收成功待建联 3揽收失败待建联 4已建联 5确认无法建联；
   * 非灰度 hub / 历史存量站点为 undefined。
   *
   * 只用于标注与禁选 —— 服务端本就拒绝把「确认无法建联(5)」的站点当作合并/AB 目标，
   * 但此前前端拿不到该字段，运营只能选中提交后才吃一个失败。
   */
  linkStatus?: number;
}

/** 快捷筛选标签 */
export type ReviewSourceFilter = 'all' | ReviewSourceTag;

/**
 * 按 addressAnalysisHash 组合分组后的一组（PRD：结果一致的组合在一起，外部加框，
 * 组合内按最早时间参与排序）。
 */
export interface ReviewPoolGroup {
  analysisHash: string;
  items: ReviewPoolItem[];
  /** 组内最早创建时间（用于组间倒序排序） */
  earliestTime: string;
}
